import { useAlerts } from "components/Alerts/AlertProvider";
import { SubmissionAlert } from "components/Submission/SubmissionAlert";
import {
  CommentThreadFragment,
  CreateSubmissionInput,
  ReviewMode,
  SignatureType,
  SubmissionDocumentType,
  Submission,
  SubmissionMethod,
  SubmissionReviewerInput,
  SubmissionStatus,
  SubmissionType,
  UpdateSubmissionInput,
  GetSubmissionQuery,
  ReviewerStatus,
} from "generated-graphql/graphql";
import {
  useCreateSubmission,
  useGetSubmission,
  useMoveToReview,
  useUpdateSubmission,
} from "hooks/useSubmissionMutations";
import { useTenant } from "hooks/useTenant";
import { compact, pick } from "lodash";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { validateReviewerDueDates } from "encamp-shared/src/submission/reviewerDueDateValidation";
import { useValidatingForm } from "./useValidatingForm";
import { useSubmissionInputValidation } from "routes/Customer/Chemicals/Report/validationHooks/useSubmissionInputValidation";
import { getReviewersSubmissionDocumentReview } from "./submission/submissionReviewHelpers";
import { CurrentUser } from "providers/user";
import {
  SubmissionReviewState,
  computeSubmissionReviewStates,
  determineReviewTurn,
} from "hooks/submission/reviewStates";

export interface SubmissionFormData {
  title: string;
  facilityIds: string[];
  dueDate?: DateTime;
  submissionType?: SubmissionType;
  recipient: string;
  submissionMethod?: SubmissionMethod;
  notes?: string;
  reviewRequired: boolean;
  signatureType: SignatureType;
  reviewMode: ReviewMode;
  reviewers: Array<{
    userId: string;
    reviewOrder: number;
    isRequired: boolean;
    isSigner: boolean;
    isStaff: boolean;
    dueDate?: DateTime;
  }>;
  comment?: string;
  tenantId: string;
}

export type SubmissionComment = CommentThreadFragment;

export interface SubmissionReviewerSigner {
  id: string;
  name: string;
  email: string;
  order: number;
  address?: string;
  dueDate?: DateTime;
  isSigner?: boolean;
  isStaff: boolean;
}

interface UseSubmissionFormProps {
  submissionId?: string;
  isEditMode?: boolean;
  onSubmit?: (submissionId: string, needsDocumentPreparation: boolean) => void;
  submissionDocumentIds?: string[];
  attachmentDocumentIds?: string[];
  virtualTaskId?: string;
  user?: CurrentUser | null;
  initialFormData?: {
    title?: string;
    facilityIds?: string[];
    dueDate?: DateTime;
    submissionType?: SubmissionType;
    notes?: string;
  };
}

function calculateReviewerNeedsDocumentPreparation(
  reviewer: Pick<
    SubmissionFormData["reviewers"][number],
    "userId" | "isSigner"
  >,
  submissionData: GetSubmissionQuery | undefined
) {
  if (!reviewer.isSigner || !submissionData) {
    return false;
  }

  const submissionDocuments =
    submissionData.submission?.reviewers.find(
      (r) => r.user.id === reviewer.userId
    )?.submissionDocumentReviews ?? [];
  return submissionDocuments.some(
    (sdr) => sdr.submissionDocument?.externalDocumentId === null
  );
}

export function useSubmissionForm({
  submissionId,
  isEditMode = false,
  onSubmit,
  submissionDocumentIds = [],
  attachmentDocumentIds = [],
  virtualTaskId,
  user,
  initialFormData,
}: UseSubmissionFormProps) {
  const alerts = useAlerts();
  const { tenantId } = useTenant();

  const [reviewers, setReviewers] = useState<SubmissionReviewerSigner[]>([]);
  const [signer, setSigner] = useState<SubmissionReviewerSigner | null>(null);
  const [staffAssignee, setStaffAssignee] = useState<{
    id: string;
    name: string;
    email: string;
  } | null>(null);
  const [comments, setComments] = useState<SubmissionComment[]>([]);
  const [needsDocumentPreparation, setNeedsDocumentPreparation] =
    useState(false);
  const [hasInitializedForm, setHasInitializedForm] = useState(false);

  const {
    data: submissionData,
    loading: loadingSubmission,
    refetch: refetchSubmission,
  } = useGetSubmission(submissionId || "", !submissionId);

  const [createSubmission, { loading: creating }] = useCreateSubmission();
  const [updateSubmissionMutation, { loading: updating }] =
    useUpdateSubmission();
  const [moveToReviewMutation, { loading: moveToReviewLoading }] =
    useMoveToReview();

  const defaultValues = useMemo<SubmissionFormData>(() => {
    if (isEditMode && submissionData?.submission) {
      const submission = submissionData.submission;
      return {
        title: submission.title || "",
        facilityIds:
          submission.facilitySubmissions?.map((f) => f.facility.id) || [],
        dueDate: submission.dueDate
          ? DateTime.fromISO(submission.dueDate)
          : undefined,
        submissionType: submission.submissionType
          ? (submission.submissionType as SubmissionType)
          : undefined,
        recipient: submission.recipient || "",
        submissionMethod:
          (submission.submissionMethod as SubmissionMethod) || undefined,
        notes: submission.notes || "",
        reviewRequired:
          submission.reviewRequired ||
          submission.signatureType !== SignatureType.None,
        signatureType: submission.signatureType || SignatureType.None,
        reviewMode: submission.reviewMode || ReviewMode.Parallel,
        reviewers: submission.reviewers.map((r) => ({
          userId: r.user.id,
          reviewOrder: r.reviewOrder,
          isRequired: r.isRequired,
          isSigner: r.isSigner,
          isStaff: r.isStaff,
          dueDate: r.dueDate ? DateTime.fromISO(r.dueDate) : undefined,
        })),
        comment: "",
        comments: submission.comments || [],
        tenantId: tenantId || "",
      };
    }

    let normalizedDueDate: DateTime | undefined = undefined;
    if (initialFormData?.dueDate) {
      const d = initialFormData.dueDate;
      if ((d as any).isLuxonDateTime) {
        normalizedDueDate = d as DateTime;
      } else if (typeof d === "string") {
        normalizedDueDate = DateTime.fromISO(d);
      } else if (d instanceof Date) {
        normalizedDueDate = DateTime.fromJSDate(d);
      }
    }

    return {
      title: initialFormData?.title || "",
      facilityIds: initialFormData?.facilityIds || [],
      dueDate: normalizedDueDate,
      submissionType: initialFormData?.submissionType ?? undefined,
      recipient: "",
      notes: initialFormData?.notes || "",
      reviewRequired: false,
      signatureType: SignatureType.None,
      reviewMode: ReviewMode.Parallel,
      reviewers: [],
      comment: "",
      comments: [],
      tenantId: tenantId || "",
    };
  }, [isEditMode, submissionData, initialFormData, tenantId]);

  const documents = useMemo(() => {
    return [
      ...submissionDocumentIds.map((d) => ({
        documentId: d,
        type: SubmissionDocumentType.Submission,
      })),
      ...attachmentDocumentIds.map((d) => ({
        documentId: d,
        type: SubmissionDocumentType.Attachment,
      })),
    ];
  }, [submissionDocumentIds, attachmentDocumentIds]);

  const form = useValidatingForm<SubmissionFormData>(
    defaultValues,
    submissionData?.submission?.issues ?? [],
    useSubmissionInputValidation(documents)
  );

  const {
    reset,
    watch,
    trigger,
    formState: { dirtyFields, errors },
    issues,
    isValidating,
  } = form;

  const initialDocIdsRef = useRef({
    submission: submissionDocumentIds,
    attachment: attachmentDocumentIds,
  });
  const hasUserTouchedDocsRef = useRef(false);
  const hasSetInitialDocIdsRef = useRef(false);

  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  // Update initialDocIdsRef when form initializes with server data in edit mode
  // This prevents validation from triggering when documents are first loaded
  // Only run once when hasInitializedForm becomes true for the first time
  useEffect(() => {
    if (isEditMode && hasInitializedForm && !hasSetInitialDocIdsRef.current) {
      initialDocIdsRef.current = {
        submission: submissionDocumentIds,
        attachment: attachmentDocumentIds,
      };
      hasUserTouchedDocsRef.current = false;
      hasSetInitialDocIdsRef.current = true;
    }
  }, [
    isEditMode,
    hasInitializedForm,
    submissionDocumentIds,
    attachmentDocumentIds,
  ]);

  /** Trigger validation when documents change, this basically makes sure that validation
   * is triggered after the documents are changed, but not on the initial render
   */
  useEffect(() => {
    const hasChanged =
      JSON.stringify(submissionDocumentIds) !==
        JSON.stringify(initialDocIdsRef.current.submission) ||
      JSON.stringify(attachmentDocumentIds) !==
        JSON.stringify(initialDocIdsRef.current.attachment);

    // Mark that user has touched documents
    if (hasChanged) {
      hasUserTouchedDocsRef.current = true;
    }

    // Validate if: user has touched docs AND (in create mode or edit is initialized)
    if (hasUserTouchedDocsRef.current && (!isEditMode || hasInitializedForm)) {
      trigger();
    }
  }, [
    documents,
    submissionDocumentIds,
    attachmentDocumentIds,
    trigger,
    isEditMode,
    hasInitializedForm,
  ]);

  const signatureType = watch("signatureType");
  const isSignatureTypeDirty = dirtyFields.signatureType;

  // Clear signer when signature mode changes to handle wet-ink vs digital requirements
  useEffect(() => {
    if (isSignatureTypeDirty) {
      setSigner(null);
    }
  }, [isSignatureTypeDirty]);

  useEffect(() => {
    if (isEditMode && submissionData?.submission && !hasInitializedForm) {
      const submission = submissionData.submission;

      if (submission.reviewers) {
        const mappedReviewers = submission.reviewers.map((r) => ({
          id: r.user.id,
          name: r.user.person
            ? `${r.user.person.first} ${r.user.person.last}`
            : r.user.email,
          email: r.user.email,
          order: r.reviewOrder,
          isSigner: r.isSigner,
          isStaff: r.isStaff,
          dueDate: r.dueDate ? DateTime.fromISO(r.dueDate) : undefined,
        }));
        const loadedReviewers = mappedReviewers.filter((r) => !r.isSigner);
        setReviewers(loadedReviewers);

        const signer = mappedReviewers.find((r) => r.isSigner);
        if (signer) {
          setSigner(signer);
        }
      }

      if (submission.staffAssignee) {
        setStaffAssignee({
          id: submission.staffAssignee.id,
          name: submission.staffAssignee.person
            ? `${submission.staffAssignee.person.first} ${submission.staffAssignee.person.last}`
            : submission.staffAssignee.email,
          email: submission.staffAssignee.email,
        });
      }

      if (submission.comments?.length > 0) {
        setComments(submission.comments);
      }

      // Delay to ensure reset has completed and prevent race conditions
      requestAnimationFrame(() => {
        setHasInitializedForm(true);
      });
    }
  }, [isEditMode, submissionData, hasInitializedForm]);

  useEffect(() => {
    if (signatureType !== SignatureType.Digital) {
      setNeedsDocumentPreparation(false);
      return;
    }
    if (!signer) {
      setNeedsDocumentPreparation(false);
      return;
    }

    if (!isEditMode && signer) {
      setNeedsDocumentPreparation(true);
      return;
    }

    if (isEditMode && signer) {
      const initialSigner = defaultValues.reviewers.find((r) => r.isSigner);
      if (
        initialSigner?.userId !== signer?.id ||
        calculateReviewerNeedsDocumentPreparation(initialSigner, submissionData)
      ) {
        setNeedsDocumentPreparation(true);
        return;
      }

      const submissionDocuments = submissionData?.submission?.documents ?? [];
      const initialServerSubmissionDocumentIds = submissionDocuments
        .filter(
          (document) => document.type === SubmissionDocumentType.Submission
        )
        .map((document) => document.document.id);

      const hasPendingClientSubmissionDocument =
        submissionDocumentIds?.some(
          (documentId) =>
            !initialServerSubmissionDocumentIds.includes(documentId)
        ) ?? false;

      if (hasPendingClientSubmissionDocument) {
        setNeedsDocumentPreparation(true);
        return;
      }

      setNeedsDocumentPreparation(false);
      return;
    }
  }, [
    isEditMode,
    defaultValues,
    signer,
    signatureType,
    submissionData,
    submissionDocumentIds,
  ]);

  const updateSubmission = useCallback(
    async (input: UpdateSubmissionInput) => {
      const result = await updateSubmissionMutation({
        variables: { input },
      });

      if (result.data?.updateSubmission) {
        const updatedSubmission = result.data.updateSubmission;
        if (
          updatedSubmission.status === "NOT_STARTED" ||
          updatedSubmission.status === "IN_PROGRESS"
        ) {
          alerts.success(
            <SubmissionAlert
              submission={updatedSubmission}
              message="This draft has successfully been saved into Upcoming Submissions."
              title="Draft saved"
              includeViewSubmissionLink={true}
            />
          );
        } else if (
          updatedSubmission.status === SubmissionStatus.EncampInitialReview ||
          updatedSubmission.status === SubmissionStatus.InReview
        ) {
          alerts.success(
            <SubmissionAlert
              submission={updatedSubmission}
              message="This submission has been sent to reviewer(s) and moved to In-Progress."
              title="Submission Sent to Reviewers"
              includeViewSubmissionLink={true}
            />
          );
        } else {
          alerts.success("Submission updated successfully");
        }
        if (onSubmit) {
          onSubmit(updatedSubmission.id, false);
        }
      }
    },
    [updateSubmissionMutation, onSubmit, alerts]
  );

  const onSubmissionSentToReviewers = useCallback(
    (updatedSubmission: Pick<Submission, "id" | "tenantId">) => {
      alerts.success(
        <SubmissionAlert
          submission={updatedSubmission}
          message="This submission has been sent to reviewer(s) and moved to In-Progress."
          title="Submission Sent to Reviewers"
          includeViewSubmissionLink={true}
        />
      );
    },
    [alerts]
  );

  const moveToReview = useCallback(
    async (input: UpdateSubmissionInput) => {
      const result = await moveToReviewMutation({
        variables: { input },
      });

      const updatedSubmission = result.data?.moveToReview;

      if (updatedSubmission) {
        if (!needsDocumentPreparation) {
          onSubmissionSentToReviewers(updatedSubmission);
        }
        if (onSubmit) {
          onSubmit(updatedSubmission.id, needsDocumentPreparation);
        }
      }
    },
    [
      moveToReviewMutation,
      onSubmit,
      needsDocumentPreparation,
      onSubmissionSentToReviewers,
    ]
  );

  const handleFormSubmit = async (
    data: SubmissionFormData,
    isMovingToReview?: boolean
  ) => {
    if (!tenantId) {
      alerts.error("Tenant ID is required");
      return;
    }

    try {
      const signerRequired = data.signatureType !== SignatureType.None;
      const signatureTypeEnum = data.signatureType;
      const allReviewers = compact([...reviewers, signer]);

      // Validate reviewer due dates (including signer due dates for Bold Sign)
      if (allReviewers.length > 0) {
        const validation = validateReviewerDueDates(
          allReviewers,
          data.dueDate,
          data.reviewMode === ReviewMode.Sequential
        );

        if (!validation.isValid) {
          validation.errors.forEach((error: string) => alerts.error(error));
          return;
        }
      }

      // Separate staff and non-staff reviewers to ensure proper ordering
      const staffReviewers = allReviewers.filter((r) => r.isStaff ?? false);
      const nonStaffReviewers = allReviewers.filter(
        (r) => !(r.isStaff ?? false)
      );

      // Map reviewers with proper reviewOrder:
      // - Staff reviewers get reviewOrder = 0
      // - Non-staff reviewers get sequential numbering (1, 2, 3...)
      const mappedReviewers: SubmissionReviewerInput[] = [
        ...staffReviewers.map((reviewer) => ({
          userId: reviewer.id,
          reviewOrder: 0,
          isStaff: true,
          isRequired: true,
          isSigner: reviewer.isSigner ?? false,
          dueDate: reviewer.dueDate?.toISO() || undefined,
        })),
        ...nonStaffReviewers.map((reviewer, index) => ({
          userId: reviewer.id,
          reviewOrder: index + 1,
          isStaff: false,
          isRequired: true,
          isSigner: reviewer.isSigner ?? false,
          dueDate: reviewer.dueDate?.toISO() || undefined,
        })),
      ];

      if (isEditMode && submissionId) {
        const input: UpdateSubmissionInput = {
          id: submissionId,
          title: data.title,
          submissionType: data.submissionType,
          recipient: data.recipient,
          submissionMethod: data.submissionMethod,
          notes: data.notes || "",
          dueDate: data.dueDate?.toISO() || undefined,
          reviewRequired: data.reviewRequired || signerRequired,
          reviewMode: data.reviewMode,
          signatureType: signatureTypeEnum,
          staffAssigneeId: staffAssignee?.id,
          reviewers: mappedReviewers,
          facilityIds: data.facilityIds,
          submissionDocumentIds: submissionDocumentIds,
          attachmentDocumentIds: attachmentDocumentIds,
        };

        if (isMovingToReview) {
          await moveToReview(input);
        } else {
          await updateSubmission(input);
        }
      } else {
        const input: CreateSubmissionInput = {
          virtualEventId: virtualTaskId || "",
          tenantId,
          title: data.title,
          submissionType: data.submissionType as SubmissionType,
          recipient: data.recipient,
          submissionMethod: data.submissionMethod as SubmissionMethod,
          notes: data.notes || "",
          dueDate: data.dueDate?.toISO() || undefined,
          reviewRequired: data.reviewRequired || signerRequired,
          reviewMode: data.reviewMode,
          signatureType: signatureTypeEnum,
          staffAssigneeId: staffAssignee?.id,
          reviewers: mappedReviewers,
          facilityIds: data.facilityIds,
          submissionDocumentIds: submissionDocumentIds,
          attachmentDocumentIds: attachmentDocumentIds,
          comments: comments.map((c) => pick(c, ["comment"])),
          moveToReview: isMovingToReview,
        };

        const result = await createSubmission({
          variables: { input },
        });

        if (result.data?.createSubmission) {
          const createdSubmission = result.data.createSubmission;
          if (
            createdSubmission.status === "NOT_STARTED" ||
            createdSubmission.status === "IN_PROGRESS"
          ) {
            alerts.success(
              <SubmissionAlert
                submission={createdSubmission}
                message="This draft has successfully been saved into Upcoming Submissions."
                title="Draft saved"
                includeViewSubmissionLink={true}
              />
            );
          } else {
            alerts.success("Submission created successfully");
          }
          if (onSubmit) {
            const prepareDocumentOnCreate = !!(
              isMovingToReview && needsDocumentPreparation
            );
            onSubmit(createdSubmission.id, prepareDocumentOnCreate);
          }
        }
      }
    } catch (error) {
      alerts.error(
        isEditMode
          ? "Failed to update submission"
          : "Failed to create submission",
        error
      );
    }
  };

  const reviewStates = useMemo(
    () => computeSubmissionReviewStates({ currentUser: user, submissionData }),
    [user, submissionData]
  );

  return {
    form,
    reviewers,
    setReviewers,
    signer,
    setSigner,
    staffAssignee,
    setStaffAssignee,
    handleFormSubmit,
    needsDocumentPreparation,
    loading: creating || updating || loadingSubmission || moveToReviewLoading,
    submissionDocuments: submissionData?.submission?.documents || [],
    submissionPayments: submissionData?.submission?.payments || [],
    submissionData,
    refetchSubmission,
    comments,
    setComments,
    hasInitializedForm,
    isFormReady:
      !isEditMode || (!!submissionData?.submission && hasInitializedForm),
    issues,
    errors,
    onSubmissionSentToReviewers,
    isValidating,
    // Review state values
    reviewState: reviewStates.reviewState,
    currentUsersSubmissionDocumentReview:
      reviewStates.currentUsersSubmissionDocumentReview,
    isReviewerASigner: reviewStates.isReviewerASigner,
    nextReviewer: reviewStates.nextReviewer,
  };
}
